import defaultSite from "./site.json";

type SiteContent = typeof defaultSite;
type Path = readonly string[];

// Apply new editorial copy only where the published CMS value is still the
// previous default. Owner-edited wording and uploaded assets stay untouched.
const previousCopy: ReadonlyArray<readonly [Path, string]> = [
  [["seo", "title", "zh"], "Asura | 香港泰國家常菜"],
  [["seo", "title", "en"], "Asura | Thai Home Cooking in Hong Kong"],
  [["seo", "description", "zh"], "Asura 把 Thai Home-cooking 泰國家常味帶到香港。從招牌泰式沾麵到一桌共享家常菜，不只冬陰功、咖喱與炒粉麵飯，更呈現泰國人日常真正會吃、會一起分享的餐桌風味。"],
  [["seo", "description", "en"], "Asura brings Thai home cooking to Hong Kong, from signature Thai dipping noodles to a shared table of dishes inspired by what Thai families actually eat at home."],
  [["hero", "subtitle", "zh"], "Asura 將泰國家常味帶到香港，從招牌沾麵到共享小菜，細味泰國餐桌的日常。"],
  [["hero", "subtitle", "en"], "Thai home cooking in Hong Kong. From signature dipping noodles to shared dishes, settle in for a taste of everyday Thailand."],
  [["philosophy", "paragraphs", "0", "zh"], "Asura 以泰國家常菜為核心，呈現泰國家庭日常享用的味道。從冬陰功、咖喱到各式家常小菜，讓親友圍坐一桌，慢慢品嚐，一同分享。"],
  [["philosophy", "paragraphs", "0", "en"], "Asura celebrates the dishes Thai families enjoy at home. From tom yum and curries to everyday small plates, our food brings friends and family together around a shared table."],
  [["menu", "intro", "zh"], "從一碗兩醬的招牌泰式沾麵，到鑊氣炒粉麵、船麵、燒物與特色小菜，每一道都是 Asura 泰國家常餐桌的一部分。菜式供應以分店即日餐牌為準。"],
  [["menu", "intro", "en"], "From our signature dipping noodles with two curry sauces to wok-fired noodles, boat noodles, grills and savoury small plates, every dish is part of the Asura home-style Thai table. Availability follows each location's daily menu."],
  [["reservations", "title", "zh"], "分店"],
  [["reservations", "title", "en"], "Locations"],
  [["reservations", "title", "zh"], "香港分店"],
  [["reservations", "title", "en"], "Hong Kong locations"],
  [["contact", "title", "zh"], "分店與最新動態"],
  [["contact", "title", "en"], "Locations & latest updates"],
  [["reservations", "body", "zh"], "從 Asura 泰沾麵、希慎廣場 Asuri，到啟德 Phaya Asura 泰公館，各品牌均以泰國家常菜為本。泰公館結合菜式、器皿與用餐氛圍，呈現完整的泰式家常用餐體驗。"],
  [["reservations", "body", "en"], "From Asura Thai Dipping Noodles and Asuri at Hysan Place to Phaya Asura in Kai Tak, each location begins with the same idea of Thai home cooking. Phaya Asura expands it into a fuller shared-table experience, shaped through flavour, tableware and the atmosphere around the table."],
  [["team", "intro", "zh"], "從一碗兩醬的沾麵，到適合分享的家常小菜，細味咖喱、鑊氣與香草交織的泰式風味。"],
  [["team", "intro", "en"], "From one bowl with two curry sauces to a table of dishes made for sharing, wok hei, curries, boat noodles and home-style small plates come together at Asura."],
  [["contact", "title", "zh"], "聯絡我們"],
  [["contact", "title", "en"], "Contact"],
  [["contact", "columns", "1", "lines", "2", "zh"], "平日 12:00-16:00 / 18:00-23:00；星期六、日及公眾假期全日營業"],
  [["contact", "columns", "1", "lines", "2", "en"], "Weekdays 12:00-16:00 / 18:00-23:00; weekends and holidays all day"],
  [["contact", "columns", "2", "lines", "0", "zh"], "希慎廣場 B2/F URBANHOOD 207號舖"],
  [["contact", "columns", "2", "lines", "0", "en"], "Shop 207, B2/F, URBANHOOD, Hysan Place"],
  [["hero", "ctaHref"], "#contact"],
  [["hero", "cta", "zh"], "查看分店"],
  [["hero", "cta", "en"], "Find a location"],
];

function readAt(value: unknown, path: Path): unknown {
  return path.reduce<unknown>((current, key) => current && typeof current === "object"
    ? (current as Record<string, unknown>)[key] : undefined, value);
}

function writeAt(value: unknown, path: Path, next: unknown) {
  const parent = readAt(value, path.slice(0, -1));
  if (parent && typeof parent === "object") (parent as Record<string, unknown>)[path.at(-1)!] = next;
}

export function upgradeSiteContent(content: SiteContent): SiteContent {
  const upgraded = structuredClone(content);
  for (const [path, previous] of previousCopy) {
    if (readAt(upgraded, path) === previous) writeAt(upgraded, path, readAt(defaultSite, path));
  }

  for (const item of upgraded.navigation) {
    if (item.id !== "visit") continue;
    if (item.label.zh === "分店與動態") item.label.zh = defaultSite.navigation.at(-1)!.label.zh;
    if (item.label.en === "Locations & updates") item.label.en = defaultSite.navigation.at(-1)!.label.en;
  }

  // The contact hub now owns its copy and actions. Remove fields from older
  // saved drafts so the CMS model cannot resurrect retired controls.
  const legacyReservations = upgraded.reservations as typeof upgraded.reservations & Record<string, unknown>;
  for (const key of ["image", "alt", "body", "cta", "href"]) delete legacyReservations[key];

  const legacyLinks = upgraded.navigation.filter((item) => item.id === "reservations" || item.id === "contact");
  if (legacyLinks.length) {
    const alreadyHasVisit = upgraded.navigation.some((item) => item.id === "visit");
    upgraded.navigation = [
      ...upgraded.navigation.filter((item) => item.id !== "reservations" && item.id !== "contact"),
      ...(alreadyHasVisit ? [] : [structuredClone(defaultSite.navigation.at(-1)!)]),
    ];
  }
  return upgraded;
}
